import {
  all, AllEffect,
  call, CallEffect,
  ForkEffect,
  put, PutEffect,
  race, RaceEffect,
  select, SelectEffect,
  take, TakeEffect,
  takeLatest,
} from 'redux-saga/effects';
import { getCart, getCartProductBySku } from 'b2b-apps/core/cart/redux/cart/selectors';
import { analyzeView } from 'b2b-apps/shop/analytics/redux/tealium/actionCreators';
import { TEALIUM_INTERACTION_ID, TEALIUM_PAGE_TYPE, TEALIUM_PROD_ACTION } from 'b2b-apps/shop/analytics/types/types';
import { preparePrice, prepareQuantity, prepareStock } from 'b2b-apps/shop/analytics/utils/analytics';
import {
  FETCH_CART_FULFILLED,
  FETCH_CART_REJECTED, REMOVE_CART_FULFILLED,
  UPDATE_ITEM_QUANTITY_FULFILLED,
  UPDATE_ITEM_QUANTITY_REJECTED,
} from 'b2b-apps/shop/cart/redux/cart/actionTypes';
import {
  FetchCartFulfilledAction,
  FetchCartRejectedAction,
  RemoveCartFulfilledAction,
  UpdateItemQuantityFulfilledAction,
  UpdateItemQuantityRejectedAction,
} from 'b2b-apps/shop/cart/redux/cart/types';
import { RemoteRecord } from 'b2b-apps/shop/common/redux/RemoteRecord';
import { CartProductType, CartType } from 'b2b-common/core/cart/api/CartTypes';
import { getInteractionProductFromCartItem } from '../products/utils';
import { ANALYZE_CART_PAGE_VIEW, ANALYZE_CLEAR_CART, ANALYZE_ITEM_CLICK, ANALYZE_ITEM_QUANTITY_CHANGE } from './actionTypes';
import { analyzeCartProductAddInteraction, analyzeCartProductClickInteraction, analyzeCartProductRemoveInteraction } from './interactions';
import { AnalyzeItemClickAction, AnalyzeItemQuantityChangeAction } from './types';

function* analyzeCartPageViewSaga(): Generator<
    SelectEffect | PutEffect| RaceEffect<TakeEffect>,
    void,
    RemoteRecord<CartType> & {fulfilled: FetchCartFulfilledAction, rejected: FetchCartRejectedAction}
    > {
  const { fulfilled, rejected } = yield race({
    fulfilled: take(FETCH_CART_FULFILLED),
    rejected: take(FETCH_CART_REJECTED),
  });

  if (rejected) {
    return;
  }

  const products = fulfilled.payload.data?.products || [];

  yield put(analyzeView({
    interaction: TEALIUM_INTERACTION_ID.cartView,
    page: {
      type: TEALIUM_PAGE_TYPE.cart,
    },
    prod: products.map(p => ({
      action: TEALIUM_PROD_ACTION.checkoutCart,
      categories: p.analytics.categories,
      id: p.sku,
      price: preparePrice(p.single.price),
      quan: prepareQuantity(p.cart.quantity),
      stock: prepareStock(p.settings.quantity),
    })),
  }));
}

function* analyzeItemClickSaga(action: AnalyzeItemClickAction):
  Generator<
    CallEffect | SelectEffect,
    void,
    CartProductType
  > {
  const { sku, listPosition } = action.payload;
  const cartProduct = yield select(getCartProductBySku, sku);

  if (!cartProduct) {
    return;
  }

  yield call(
    analyzeCartProductClickInteraction,
    { ...getInteractionProductFromCartItem(cartProduct), listPosition },
  );
}

function* analyzeItemQuantityChangeSaga(
  action: AnalyzeItemQuantityChangeAction,
): Generator<
  CallEffect | SelectEffect | RaceEffect<TakeEffect>,
  void,
  & CartProductType
  & { fulfilled: UpdateItemQuantityFulfilledAction, rejected: UpdateItemQuantityRejectedAction }
  > {
  const { sku, quantity, listPosition } = action.payload;
  const cartProduct = yield select(getCartProductBySku, sku);

  if (!cartProduct) {
    return;
  }

  const { rejected } = yield race({
    fulfilled: take(UPDATE_ITEM_QUANTITY_FULFILLED),
    rejected: take(UPDATE_ITEM_QUANTITY_REJECTED),
  });

  if (rejected) {
    return;
  }

  const product = {
    ...getInteractionProductFromCartItem(cartProduct),
    listPosition,
    quantity: Math.abs(quantity),
  };

  if (quantity < 0) {
    yield call(analyzeCartProductRemoveInteraction, [product]);
    return;
  }

  yield call(analyzeCartProductAddInteraction, [product]);
}

function* analyzeClearCartSaga(): Generator<
  CallEffect | SelectEffect | RaceEffect<TakeEffect>,
  void,
  & RemoteRecord<CartType>
  & { fulfilled: RemoveCartFulfilledAction, rejected: FetchCartRejectedAction }
  > {
  const cartRecord = yield select(getCart);
  const cart = cartRecord.data;

  if (!cart) {
    return;
  }

  const { rejected } = yield race({
    fulfilled: take(REMOVE_CART_FULFILLED),
    rejected: take(FETCH_CART_REJECTED),
  });

  if (rejected) {
    return;
  }

  const products = cart.products.map((product, index) => ({
    ...getInteractionProductFromCartItem(product),
    quantity: product.settings.quantity,
    listPosition: index + 1,
  }));

  yield call(
    analyzeCartProductRemoveInteraction,
    products,
  );
}

export function* cartAnalyticsSaga(): Generator<AllEffect<ForkEffect>, void> {
  yield all([
    takeLatest(ANALYZE_CART_PAGE_VIEW, analyzeCartPageViewSaga),
    takeLatest(ANALYZE_ITEM_CLICK, analyzeItemClickSaga),
    takeLatest(ANALYZE_ITEM_QUANTITY_CHANGE, analyzeItemQuantityChangeSaga),
    takeLatest(ANALYZE_CLEAR_CART, analyzeClearCartSaga),
  ]);
}

